import { supabase } from '../config/supabase';

export interface Attachment {
    id: string;
    ticketId: string;
    fileName: string;
    fileUrl: string;
    fileType: string;
    fileSize: number;
    uploadedBy: string;
    createdAt: Date;
}

/**
 * Service for managing ticket attachments and file uploads
 */
export class AttachmentService {
    private static readonly BUCKET_NAME = 'ticket-attachments';
    private static readonly MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
    private static readonly ALLOWED_FILE_TYPES = [
        'image/jpeg',
        'image/png',
        'image/gif',
        'image/webp',
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/plain',
        'text/csv'
    ];

    /**
     * Upload a file to Supabase Storage and create attachment record
     */
    static async uploadFile(
        ticketId: string,
        file: File,
        userId: string
    ): Promise<Attachment | null> {
        try {
            // Validate file size
            if (file.size > this.MAX_FILE_SIZE) {
                throw new Error(`El archivo excede el tamaño máximo permitido de ${this.MAX_FILE_SIZE / 1024 / 1024}MB`);
            }

            // Validate file type
            if (!this.ALLOWED_FILE_TYPES.includes(file.type)) {
                throw new Error('Tipo de archivo no permitido');
            }

            // Generate unique file path
            const fileExt = file.name.split('.').pop();
            const fileName = `${ticketId}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

            // Upload to Supabase Storage
            const { data: uploadData, error: uploadError } = await supabase.storage
                .from(this.BUCKET_NAME)
                .upload(fileName, file, {
                    cacheControl: '3600',
                    upsert: false
                });

            if (uploadError) throw uploadError;

            // Get public URL
            const { data: { publicUrl } } = supabase.storage
                .from(this.BUCKET_NAME)
                .getPublicUrl(fileName);

            // Create attachment record in database
            const { data: attachmentData, error: dbError } = await supabase
                .from('attachments')
                .insert({
                    ticket_id: ticketId,
                    file_name: file.name,
                    file_url: publicUrl,
                    file_type: file.type,
                    file_size: file.size,
                    uploaded_by: userId
                })
                .select()
                .single();

            if (dbError) throw dbError;

            // Map to Attachment type
            return this.mapToAttachment(attachmentData);
        } catch (error) {
            console.error('Error uploading file:', error);
            throw error;
        }
    }

    /**
     * Get all attachments for a ticket
     */
    static async getAttachments(ticketId: string): Promise<Attachment[]> {
        try {
            const { data, error } = await supabase
                .from('attachments')
                .select('*')
                .eq('ticket_id', ticketId)
                .order('created_at', { ascending: false });

            if (error) throw error;

            return data.map(this.mapToAttachment);
        } catch (error) {
            console.error('Error getting attachments:', error);
            return [];
        }
    }

    /**
     * Delete an attachment
     */
    static async deleteAttachment(attachmentId: string): Promise<boolean> {
        try {
            // Get attachment details first
            const { data: attachment, error: fetchError } = await supabase
                .from('attachments')
                .select('*')
                .eq('id', attachmentId)
                .single();

            if (fetchError) throw fetchError;

            // Extract file path from URL
            const urlParts = attachment.file_url.split('/');
            const filePath = urlParts.slice(-2).join('/'); // Get ticketId/filename

            // Delete from storage
            const { error: storageError } = await supabase.storage
                .from(this.BUCKET_NAME)
                .remove([filePath]);

            if (storageError) console.warn('Error deleting from storage:', storageError);

            // Delete from database
            const { error: dbError } = await supabase
                .from('attachments')
                .delete()
                .eq('id', attachmentId);

            if (dbError) throw dbError;

            return true;
        } catch (error) {
            console.error('Error deleting attachment:', error);
            return false;
        }
    }

    /**
     * Map database record to Attachment type
     */
    private static mapToAttachment(data: any): Attachment {
        return {
            id: data.id,
            ticketId: data.ticket_id,
            fileName: data.file_name,
            fileUrl: data.file_url,
            fileType: data.file_type,
            fileSize: data.file_size,
            uploadedBy: data.uploaded_by,
            createdAt: new Date(data.created_at)
        };
    }

    /**
     * Format file size for display
     */
    static formatFileSize(bytes: number): string {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    }

    /**
     * Get file icon based on file type
     */
    static getFileIcon(fileType: string): string {
        if (fileType.startsWith('image/')) return '🖼️';
        if (fileType === 'application/pdf') return '📄';
        if (fileType.includes('word')) return '📝';
        if (fileType.includes('excel') || fileType.includes('spreadsheet')) return '📊';
        if (fileType.startsWith('text/')) return '📃';
        return '📎';
    }
}
